import {
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Close, KeyboardArrowDown } from '@material-ui/icons';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from 'react';
// local
import { SearchBy, SearchField } from '../../types/commonTypes';
import { SearchByMenu } from '../../views/Manage/Account/Header/SearchByMenu';

interface Props {
  clearOnBlur?: boolean;
  clearOnSelect?: boolean;
  disabled?: boolean;
  groupBy?(option: any): string;
  loading?: boolean;
  loadingText?: string;
  optionLabel?(option: any): string;
  options: any[];
  resultLimit?: number;
  searchFields: SearchField[];
  onSearchByChange(searchBy: SearchBy): void;
  onSearch(value: any, searchBy: SearchBy): void;
  setSearchInUse(using: boolean): void;
}

export const DynamicSearchBar = (props: Props) => {
  const {
    clearOnBlur = false,
    clearOnSelect = false,
    disabled = false,
    groupBy,
    loading = false,
    loadingText = 'Loading...',
    onSearch,
    onSearchByChange,
    options,
    optionLabel = (option) => option,
    resultLimit,
    searchFields,
    setSearchInUse,
  } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [searchBy, setSearchBy] = useState(searchFields[0]);
  const [searchValue, setSearchValue] = useState<any>(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (inputValue === '') {
      setSearchInUse(false);
      onSearch(null, searchBy.value);
    }
  }, [inputValue]);

  const changeSearchBy = (searchByValue: SearchBy) => {
    let newSearchBy = searchFields.find((f) => f.value == searchByValue);

    if (newSearchBy && newSearchBy.value !== searchBy.value) {
      setAnchorEl(null);
      setSearchBy(newSearchBy);
    } else {
      setAnchorEl(null);
    }
    onSearchByChange(searchByValue);
  };

  const handleAutocompleteChange = (event: ChangeEvent<{}>, value: any) => {
    if (clearOnSelect) {
      setInputValue('');
    }

    setSearchValue(value || '');
    if (value) onSearch(value, searchBy.value);
  };

  const openSearchByMenu = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const submitForm = (event: FormEvent<HTMLDivElement>, value: string) => {
    event.preventDefault();
    onSearch(value, searchBy.value);
  };

  return (
    <>
      <Paper
        className={classes.root}
        onSubmit={(event) => submitForm(event, searchValue)}
      >
        <Tooltip title="Search By">
          <span>
            <IconButton
              onClick={openSearchByMenu}
              className={classes.iconButton}
              disabled={loading || disabled}
            >
              <KeyboardArrowDown />
            </IconButton>
          </span>
        </Tooltip>

        <Autocomplete
          id="dynamic-search"
          onClose={() => {
            setSearchValue(searchValue);
          }}
          classes={{
            groupLabel: classes.groupLabel,
          }}
          clearOnBlur={clearOnBlur}
          disabled={loading || disabled}
          fullWidth
          value={searchValue}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          onChange={handleAutocompleteChange}
          options={options}
          popupIcon={null}
          filterOptions={createFilterOptions({
            limit: resultLimit,
          })}
          getOptionLabel={optionLabel}
          groupBy={groupBy}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={loading ? loadingText : searchBy.label}
            />
          )}
        />
        {inputValue !== '' ? (
          <IconButton
            className={classes.close}
            disableRipple
            onClick={() => {
              setInputValue('');
              setSearchInUse(false);
              onSearch(null, searchBy.value);
            }}
          >
            <Close />
          </IconButton>
        ) : null}
      </Paper>
      <SearchByMenu
        anchorEl={anchorEl}
        changeSearchBy={changeSearchBy}
        fields={searchFields}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  close: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    padding: 0,
  },
  root: {
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 200,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  groupLabel: {
    color: theme.palette.primary.main,
  },
  iconButton: {
    padding: theme.spacing(1),
  },
}));
